import { FollowerTemplateSchema, type FollowerTemplate } from '../../schemas/follower';

/**
 * Template del **Famiglio del Mago** (manuale V1, rr. 4186-4204).
 *
 * Secondo template Follower del sistema unificato FI-8 (dopo il pilota
 * `ranger-compagno-animale` di FI-8a). Esercita per la prima volta:
 *  - **`peculiaritiesFromChoiceGroup`** con 6 mapping eterogenei
 *    (`skill-rank-cumulative`, `skill-rank-from-narrative`,
 *    `modifier-from-narrative`, `narrative-trait`×2, `custom-trait-from-narrative`)
 *  - **`hpRule.fixed`** (5 PF a livello 3, non scalano col livello PG)
 *  - **`fixedSkills`** popolato (Adepto R1 preset, prima di eventuali
 *    miglioramenti via peculiarità `adepto-rango-2`)
 *  - **Pattern duplicabile** consumando `MoveChoice.allowDuplicate: true`
 *    introdotto in D1: scelte 2× su una stessa opzione producono effetto
 *    cumulativo (Adepto R2→R3, altra-Abilità R1→R2, mod +2→+3) oppure
 *    trait separati (capacità custom).
 *
 * Stats fisse (manuale r. 4189): livello 3, +1 a tutti i modificatori,
 * 5 PF, Carico 3, d4 come dado di Danno. Adepto a Rango 1 preset; il
 * giocatore può potenziarlo a R2/R3 via la peculiarità `adepto-rango-2`.
 *
 * Niente `capabilityCaps`: le peculiarità sono discrete e cumulative al
 * massimo R3 (capped dallo schema) o +3 (capped dallo schema modifier).
 *
 * Riguardo i prompt enum-condizionati (FI-8b D1.5-Q2):
 *  - `quale-altra-abilita`: `oneOf` con le 8 Abilità non-Adepto (manuale r.
 *    4194: «un'altra Abilità»). Il `*Second` non serve perché la peculiarità
 *    è cumulativa sulla stessa Abilità.
 *  - `quale-modificatore`: `oneOf` con le 6 stat. `*Second` non necessario
 *    per lo stesso motivo (cumulativa sulla stessa stat).
 *  - `capacita-utile-{label,desc}-{1,2}`: i 4 narrativeField per le 2
 *    occorrenze indipendenti della peculiarità custom.
 */
export const magoFamiglioTemplate: FollowerTemplate = FollowerTemplateSchema.parse({
  id: 'mago-famiglio',
  sourceMoveId: 'mago.famiglio',
  type: 'animal-companion',
  label: 'Famiglio',
  baseStats: {
    level: 3,
    modifiers: {
      forza: 1,
      destrezza: 1,
      costituzione: 1,
      intelligenza: 1,
      saggezza: 1,
      carisma: 1,
    },
    hpRule: { type: 'fixed', value: 5 },
    damageDie: 'd4',
    load: 3,
    tags: [],
  },
  fixedSkills: [{ abilityId: 'adepto', rank: 1, isPrimary: true }],
  fixedTraits: [],
  peculiaritiesFromChoiceGroup: {
    fromChoiceGroup: 'main',
    optionToEffect: {
      'adepto-rango-2': {
        kind: 'skill-rank-cumulative',
        abilityId: 'adepto',
        baseRank: 2,
        isPrimary: true,
      },
      'altra-abilita-rango-1': {
        kind: 'skill-rank-from-narrative',
        narrativeFieldId: 'quale-altra-abilita',
        baseRank: 1,
        isPrimary: false,
      },
      'modificatore-piu-2': {
        kind: 'modifier-from-narrative',
        narrativeFieldId: 'quale-modificatore',
        baseAmount: 2,
      },
      'parla-linguaggio-comune': {
        kind: 'narrative-trait',
        traitId: 'famiglio-linguaggio-comune',
        label: 'Linguaggio comprensibile',
        description: 'Il Famiglio sa parlare un linguaggio comprensibile ai più.',
      },
      telepatia: {
        kind: 'narrative-trait',
        traitId: 'famiglio-telepatia',
        label: 'Comunicazione telepatica',
        description: 'Il Mago e il Famiglio comunicano telepaticamente tra loro.',
      },
      'capacita-utile': {
        kind: 'custom-trait-from-narrative',
        labelFieldId: 'capacita-utile-label-1',
        descriptionFieldId: 'capacita-utile-desc-1',
        labelFieldIdSecond: 'capacita-utile-label-2',
        descriptionFieldIdSecond: 'capacita-utile-desc-2',
      },
    },
  },
  nameFromNarrativeField: 'nome-famiglio',
  respawnRule:
    'Quando il Famiglio raggiunge 0 PF, muore. Per richiamarlo: compi un nuovo rituale di evocazione + scegli 3 condizioni dalla lista di *Magia Improvvisata*. Se il GM approva, ogni rituale concede una capacità utile a sua scelta o una peculiarità aggiuntiva oltre alle due di base.',
});
